/**
 * Wire types every integration shares: whether a capability can be relied on,
 * how a connection's traffic stands, what it has received, and the six frames
 * the Integrations console hears (PRD-0401 D-13, D-19, D-20; PRD-0402 D-22,
 * D-24).
 *
 * Snake-case-verbatim mirrors of Backend/app/schemas/integrations/ and the
 * closed vocabularies in Backend/app/contracts/integrations/. A capability
 * carries both `state` and `readiness` because they answer different
 * questions: the state is the stored proof, and the readiness is that proof
 * read against its owner's configuration and its expiry, so a paused channel
 * whose proof stands reads `disabled` rather than `ready`.
 *
 * Backend/tests/contract/test_integrations_wire_parity.py pins these mirrors
 * to their Python owners, as it does each provider's shapes.
 */

import type {
  JenkinsConnectionChangedPayload,
  JenkinsJobBindingChangedPayload,
} from './jenkins'
import type {
  SlackConnectionChangedPayload,
  SlackDestinationChangedPayload,
  SlackErrorCode,
} from './slack'

// ---------------------------------------------------------------------------
// Closed vocabularies
// ---------------------------------------------------------------------------

/** The external systems whose connections the integrations registry governs. */
export type IntegrationProvider = 'slack' | 'jenkins'

/** The kind of row a capability's evidence is kept against; each names its provider. */
export type CapabilitySubjectKind =
  | 'slack_connection'
  | 'slack_destination'
  | 'jenkins_credential'

/** One provable ability. A token that authenticates proves nothing about posting. */
export type CapabilityName = 'credential' | 'post' | 'publish'

/** Where a capability's stored proof stands; `verifying` is a worker's claim on a request. */
export type EvidenceState = 'unverified' | 'pending' | 'verifying' | 'ready' | 'failed'

/**
 * Whether a capability can be relied on now. `pending` covers a waiting request
 * and a check in flight alike, `stale` is a proof past its expiry, and
 * `disabled` is a subject its owner has turned off, whatever its proof says.
 */
export type CapabilityReadiness =
  | 'unverified'
  | 'pending'
  | 'ready'
  | 'stale'
  | 'failed'
  | 'disabled'
  | 'unsupported'

/** Which path wrote a capability's latest result. */
export type EvidenceSource = 'verification' | 'delivery' | 'receipt'

/**
 * Every reason of Jenkins' own that a Jenkins capability can give for not
 * being ready: a publisher test whose challenge expired before an answer came.
 */
export type JenkinsErrorCode = 'test_expired'

/**
 * Every reason a capability can give for not being ready: each provider's
 * classified failures, and a capability no provider declares as verifiable.
 */
export type IntegrationReasonCode = SlackErrorCode | JenkinsErrorCode | 'unsupported'

/** Traffic Alveary sends to a provider, or receives from one. */
export type DeliveryDirection = 'inbound' | 'outbound'

/**
 * Where one inbound receipt stands. `received` waits for the job runner, and
 * one that has spent attempts is waiting to be tried again; `processing` is a
 * worker's claim. The other three are terminal until an administrator's
 * replay returns a failed or discarded receipt to `received`.
 */
export type InboundReceiptStatus =
  | 'received'
  | 'processing'
  | 'processed'
  | 'failed'
  | 'discarded'

/**
 * Why a receipt was not processed: the first four discard it at the authority
 * recheck, and the last two fail it.
 */
export type InboundOutcomeCode =
  | 'binding_revoked'
  | 'connection_disabled'
  | 'superseded'
  | 'credential_revoked'
  | 'attempts_exhausted'
  | 'invalid_payload'

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/** One capability's proof for one connection or resource, and its readiness. */
export interface IntegrationCapability {
  id: string
  provider: IntegrationProvider
  /** The governing connection, which a resource's evidence is kept under too. */
  connection_id: string
  subject_kind: CapabilitySubjectKind
  subject_id: string
  capability: CapabilityName
  readiness: CapabilityReadiness
  state: EvidenceState
  /** Moves on every request and reset, so a result for an older one is refused. */
  generation: number
  /** When the latest result was observed; null until one has been. */
  observed_at: string | null
  /** The last proof, kept through a failure and cleared by a reset. */
  last_ready_at: string | null
  expires_at: string | null
  reason_code: IntegrationReasonCode | null
  source: EvidenceSource | null
  /** Non-secret provider facts, such as the scopes a Slack bot lacks. */
  details: Record<string, unknown>
}

/** The house five-field page envelope over capability rows. */
export interface IntegrationCapabilityListResponse {
  items: IntegrationCapability[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

// ---------------------------------------------------------------------------
// Communication
// ---------------------------------------------------------------------------

/**
 * How one connection's traffic in one direction stands, in counts, never
 * content. `ready` rows can be claimed now, `waiting` rows are due later
 * behind a backoff or a hold, and `in_flight` rows are held by a worker; the
 * two recent counts are the terminal exceptions of the last `window_days`.
 */
export interface IntegrationCommunicationSummary {
  provider: IntegrationProvider
  /** Null for traffic whose resource has been deleted, so it is still counted. */
  connection_id: string | null
  direction: DeliveryDirection
  ready: number
  waiting: number
  in_flight: number
  failed_recent: number
  uncertain_recent: number
  /** When the longest-waiting claimable row became claimable. */
  oldest_ready_at: string | null
  window_days: number
}

/** The house five-field page envelope over communication summaries. */
export interface IntegrationCommunicationListResponse {
  items: IntegrationCommunicationSummary[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

/**
 * One inbound receipt: its identity, state, outcome, attempts and times, never
 * its payload. `attribution` holds the ids admission verified, so a reader can
 * tell which credential and binding admitted a receipt that never reached a
 * project, and `payload_held` says whether what a replay needs is still held
 * or retention has purged it.
 */
export interface IntegrationReceipt {
  id: string
  provider: IntegrationProvider
  connection_id: string
  /** The event key every retry and resend of the same event repeats. */
  external_id: string
  event_type: string
  attribution: Record<string, unknown>
  status: InboundReceiptStatus
  outcome_code: InboundOutcomeCode | null
  /** Null until processing resolves the receipt's project, and for one that never reaches one. */
  project_id: string | null
  payload_bytes: number
  payload_held: boolean
  attempt_count: number
  received_at: string
  processed_at: string | null
}

/** The house five-field page envelope over one connection's receipts, newest first. */
export interface IntegrationReceiptListResponse {
  items: IntegrationReceipt[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

/** The provider whose table holds the receipt an administrator replays, since each keeps its own. */
export interface IntegrationReceiptReplayRequest {
  provider: IntegrationProvider
}

/**
 * Why a replay was refused with a 409, as its `detail.reason`: the receipt was
 * processed, is still waiting or held by a worker, or retention has purged the
 * payload a run would use. Mirrors the backend contract in
 * `contracts/integrations/receipt_replay_refusal.py`, pinned by the integrations
 * wire parity test.
 */
export type IntegrationReceiptReplayRefusal =
  | 'already_processed'
  | 'not_settled'
  | 'payload_purged'

// ---------------------------------------------------------------------------
// Read windows
// ---------------------------------------------------------------------------

/** The capability read's window; the server bounds `limit` at 500. */
export interface IntegrationCapabilityFilters {
  provider?: IntegrationProvider
  connection_id?: string
  limit?: number
  offset?: number
}

/** The communication read's filter: one provider's connections, or every provider's. */
export interface IntegrationCommunicationFilters {
  provider?: IntegrationProvider
}

/** The receipt read's window over one connection; the server bounds `limit` at 200. */
export interface IntegrationReceiptFilters {
  provider: IntegrationProvider
  connection_id: string
  limit?: number
  offset?: number
}

// ---------------------------------------------------------------------------
// Broadcasts — identity only
// ---------------------------------------------------------------------------

/**
 * The six broadcasts the Integrations console hears in the global room: the
 * two Slack registry frames, the two Jenkins registry frames, a capability's
 * evidence moving, and a connection's traffic moving. Exported so the
 * consuming hook's constants and the roster pin share one list.
 */
export const INTEGRATION_EVENTS = [
  'slack_connection_changed',
  'slack_destination_changed',
  'jenkins_connection_changed',
  'jenkins_job_binding_changed',
  'integration_capability_changed',
  'integration_delivery_changed',
] as const

export type IntegrationEventName = (typeof INTEGRATION_EVENTS)[number]

/** Which capability's evidence changed, under which connection. */
export interface IntegrationCapabilityChangedPayload {
  version: 1
  provider: IntegrationProvider
  connection_id: string
  subject_kind: CapabilitySubjectKind
  subject_id: string
  capability: CapabilityName
}

/**
 * Which connection's traffic moved in which direction. One frame stands for a
 * window of changes, so it names no row.
 */
export interface IntegrationDeliveryChangedPayload {
  version: 1
  provider: IntegrationProvider
  connection_id: string | null
  direction: DeliveryDirection
}

/** The payload shapes the six broadcasts carry, in the roster's own order. */
export type IntegrationEventPayload =
  | SlackConnectionChangedPayload
  | SlackDestinationChangedPayload
  | JenkinsConnectionChangedPayload
  | JenkinsJobBindingChangedPayload
  | IntegrationCapabilityChangedPayload
  | IntegrationDeliveryChangedPayload
